export type MarketingSetupStatus = {
  posthog: {
    configured: boolean;
    projectTokenConfigured: boolean;
    hostConfigured: boolean;
    host: string | null;
    missing: string[];
  };
  siteVerification: {
    googleConfigured: boolean;
    bingConfigured: boolean;
    missing: string[];
  };
};

export function cleanMarketingEnv(value: string | undefined): string | undefined {
  const cleaned = value?.trim().replace(/^["']|["']$/g, "").trim();
  return cleaned || undefined;
}

type MarketingEnv = Record<string, string | undefined> & Partial<
  Record<
    | "NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN"
    | "NEXT_PUBLIC_POSTHOG_HOST"
    | "NEXT_PUBLIC_GOOGLE_ADS_ID"
    | "NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION"
    | "NEXT_PUBLIC_BING_SITE_VERIFICATION",
    string | undefined
  >
>;

export const DEFAULT_GOOGLE_ADS_ID = "AW-18203719287";

const GOOGLE_ADS_ID_PATTERN = /^AW-\d+$/;

export function googleAdsIdFromEnv(
  env: Pick<MarketingEnv, "NEXT_PUBLIC_GOOGLE_ADS_ID"> = {
    NEXT_PUBLIC_GOOGLE_ADS_ID: process.env.NEXT_PUBLIC_GOOGLE_ADS_ID,
  },
): string {
  const configuredId = cleanMarketingEnv(env.NEXT_PUBLIC_GOOGLE_ADS_ID);
  if (configuredId && GOOGLE_ADS_ID_PATTERN.test(configuredId)) return configuredId;
  return DEFAULT_GOOGLE_ADS_ID;
}

export function marketingSetupStatus(env: MarketingEnv = process.env): MarketingSetupStatus {
  const posthogProjectToken = cleanMarketingEnv(env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN);
  const posthogHost = cleanMarketingEnv(env.NEXT_PUBLIC_POSTHOG_HOST)?.replace(/\/+$/, "");
  const googleVerification = cleanMarketingEnv(env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION);
  const bingVerification = cleanMarketingEnv(env.NEXT_PUBLIC_BING_SITE_VERIFICATION);

  const posthogMissing = [
    !posthogProjectToken ? "NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN" : null,
    !posthogHost ? "NEXT_PUBLIC_POSTHOG_HOST" : null,
  ].filter((value): value is string => Boolean(value));
  const verificationMissing = [
    !googleVerification ? "NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION" : null,
    !bingVerification ? "NEXT_PUBLIC_BING_SITE_VERIFICATION" : null,
  ].filter((value): value is string => Boolean(value));

  return {
    posthog: {
      configured: posthogMissing.length === 0,
      projectTokenConfigured: Boolean(posthogProjectToken),
      hostConfigured: Boolean(posthogHost),
      host: posthogHost ?? null,
      missing: posthogMissing,
    },
    siteVerification: {
      googleConfigured: Boolean(googleVerification),
      bingConfigured: Boolean(bingVerification),
      missing: verificationMissing,
    },
  };
}
